import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import {
  isTransientCursorError,
  useSafeLiveQuery,
} from "@/indexedDB/hooks/useSafeLiveQuery";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { debounce } from "lodash";
// Month formatting removed as we no longer group by month
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import {
  getLatestMessagesWithMediaByChatIdFromLocalDB,
  getMessagesWithMediaCountByChatIdFromLocalDB,
} from "@/indexedDB/services/messages";

const LOADING_DELAY = 2000;

/**
 * Custom hook for managing media attachments
 * Handles loading, pagination, and grouping of media attachments
 */
export function useMediaAttachments(activeChatId: string | null) {
  // State for pagination and UI states
  const [messagesTake, setMessagesTake] = useState(MESSAGE_LIST_PAGE_SIZE);
  const [isFullMediaListLoaded, setIsFullMediaListLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Create a debounced version of setIsLoading(false) with a 2-second delay
  // to prevent loading state flickering
  const debouncedSetLoadingFalse = useMemo(
    () => debounce(() => setIsLoading(false), LOADING_DELAY),
    [],
  );

  // Enhanced setIsLoading function that handles overlapping state changes
  const setLoadingState = useCallback(
    (isLoadingState: boolean) => {
      if (isLoadingState) {
        // Cancel any pending "set to false" operations when setting to true
        debouncedSetLoadingFalse.cancel();
        setIsLoading(true);
      } else {
        // Use the debounced version when setting to false
        debouncedSetLoadingFalse();
      }
    },
    [debouncedSetLoadingFalse],
  );

  // Refs for scroll handling
  const scrollerRef = useRef<HTMLDivElement>(null);
  const attachmentsGridRef = useRef<HTMLDivElement>(null);

  // Intersection observer for top anchor (loading more attachments)
  const [anchorRef, topAnchorEntry] = useIntersectionObserver({
    threshold: 0,
    root: scrollerRef.current,
    rootMargin: "0px",
  });

  // Live query messages with media attachments from IndexedDB
  // Updates automatically when messages change
  const messagesWithMedia = useSafeLiveQuery(
    async () => {
      if (!activeChatId) {
        return [];
      }

      try {
        setLoadingState(true);
        const result = await getLatestMessagesWithMediaByChatIdFromLocalDB(
          activeChatId,
          messagesTake,
        );
        setLoadingState(false);
        return result;
      } catch (err) {
        setLoadingState(false);
        // Let the known WebKit cursor error reach useSafeLiveQuery so it can
        // serve last-good + force a fresh subscription; only surface other
        // errors as a tab-level UI error.
        if (isTransientCursorError(err)) {
          throw err;
        }
        setError(err as Error);
        return [];
      }
    },
    [activeChatId, messagesTake],
    [],
  );

  // Get total count of messages with media for pagination
  const messagesWithMediaCount = useSafeLiveQuery(
    () =>
      activeChatId
        ? getMessagesWithMediaCountByChatIdFromLocalDB(activeChatId)
        : 0,
    [activeChatId],
    0,
  );

  // We no longer need to group messages by month
  // Messages are already sorted by timestamp (newest first) from the database query

  // Load more messages with media when scrolling to the top
  const loadMoreMessages = useCallback(() => {
    if (!attachmentsGridRef.current) {
      return;
    }
    if (!activeChatId) {
      return;
    }
    if (messagesWithMedia.length === 0) {
      return;
    }
    if (!messagesWithMediaCount || messagesWithMediaCount === 0) {
      return;
    }
    if (!messagesTake) {
      return;
    }

    // Check if we've loaded all messages with media
    if (messagesTake >= messagesWithMediaCount) {
      setIsFullMediaListLoaded(true);
      return;
    }

    // Load more messages with media
    if (messagesTake < messagesWithMediaCount) {
      setMessagesTake(messagesTake + MESSAGE_LIST_PAGE_SIZE);
    }
  }, [
    activeChatId,
    messagesWithMedia.length,
    messagesWithMediaCount,
    messagesTake,
    attachmentsGridRef,
  ]);

  // Create a debounced version of loadMoreMessages for better performance
  const debouncedLoadMore = useMemo(() => {
    return debounce(() => {
      if (topAnchorEntry?.isIntersecting) {
        loadMoreMessages();
      }
    }, 200);
  }, [topAnchorEntry, loadMoreMessages]);

  // Load more messages with media when top anchor is visible
  useEffect(() => {
    debouncedLoadMore();
    return () => debouncedLoadMore.cancel();
  }, [debouncedLoadMore]);

  // Handle retry loading
  const handleRetry = useCallback(() => {
    if (!activeChatId) {
      return;
    }

    setError(null);
    setLoadingState(true);

    getLatestMessagesWithMediaByChatIdFromLocalDB(activeChatId, messagesTake)
      .then(() => {
        setLoadingState(false);
      })
      .catch((err) => {
        setLoadingState(false);
        setError(err as Error);
      });
  }, [activeChatId, messagesTake, setLoadingState]);

  // Cancel any pending debounced calls when unmounting
  useEffect(() => {
    return () => debouncedSetLoadingFalse.cancel();
  }, [debouncedSetLoadingFalse]);

  return {
    isLoading,
    error,
    messagesWithMedia,
    isFullMediaListLoaded,
    scrollerRef,
    attachmentsGridRef,
    anchorRef,
    handleRetry,
  };
}
